import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { PAGE_SEO, DEFAULT_TITLE, DEFAULT_DESCRIPTION, SITE } from '../constants/seo';

// ---------------------------------------------------------------------------
// Per-page title, description, canonical and social card.
//
// A single-page app serves ONE index.html to every route, so without this
// every page in the search index carries the home page's title and
// description. Google renders JavaScript and will pick these up, but only
// because something sets them — nothing does it for free.
//
// Written by hand rather than with react-helmet: this needs four tags and a
// canonical link, and a dependency that ships a whole context provider and a
// server renderer to do it would be more code, not less.
//
// THE CANONICAL MATTERS MORE THAN IT LOOKS. /pricing, /pricing?ref=x and
// /pricing#faq are one page with three URLs, and without a canonical each can
// be indexed separately, splitting whatever authority the page has earned
// three ways.
// ---------------------------------------------------------------------------

/** Creates the tag if it is missing, so this works on a bare document too. */
const meta = (selector, attrs) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
    document.head.appendChild(el);
  }
  return el;
};

const link = (rel) => {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  return el;
};

export default function Seo({ title, description, noindex = false }) {
  const { pathname } = useLocation();

  useEffect(() => {
    const page = PAGE_SEO[pathname] || {};
    const finalTitle = title || page.title || DEFAULT_TITLE;
    const finalDescription = description || page.description || DEFAULT_DESCRIPTION;
    // Query strings and hashes are the same page; the canonical says so.
    const url = `${SITE}${pathname === '/' ? '' : pathname}`;

    document.title = finalTitle;
    meta('meta[name="description"]', { name: 'description' }).setAttribute('content', finalDescription);
    link('canonical').setAttribute('href', url);

    // Open Graph and Twitter, so a link pasted into WhatsApp — which is where
    // Indian sellers actually share things — renders as a card rather than a
    // bare URL.
    meta('meta[property="og:title"]', { property: 'og:title' }).setAttribute('content', finalTitle);
    meta('meta[property="og:description"]', { property: 'og:description' }).setAttribute('content', finalDescription);
    meta('meta[property="og:url"]', { property: 'og:url' }).setAttribute('content', url);
    meta('meta[name="twitter:title"]', { name: 'twitter:title' }).setAttribute('content', finalTitle);
    meta('meta[name="twitter:description"]', { name: 'twitter:description' }).setAttribute('content', finalDescription);

    // Signed-in screens must never be indexed: they are behind auth, so a
    // crawler would only ever record the login redirect — an empty result
    // pointing at a page nobody can open.
    meta('meta[name="robots"]', { name: 'robots' })
      .setAttribute('content', noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large');
  }, [pathname, title, description, noindex]);

  return null;
}

/**
 * A block of JSON-LD.
 *
 * Removed on unmount rather than left behind: two FAQPage blocks on one
 * document is a structured-data error, and that is exactly what happens when a
 * single-page app navigates away without cleaning up.
 */
export function JsonLd({ schema, id }) {
  useEffect(() => {
    if (!schema) return undefined;
    const el = document.createElement('script');
    el.type = 'application/ld+json';
    if (id) el.id = id;
    el.textContent = JSON.stringify(schema);
    document.head.appendChild(el);
    return () => { el.remove(); };
  }, [schema, id]);

  return null;
}
